import * as React from "react";
import {
    List,
    Datagrid,
    TextField,
    DateField,
    Edit,
    NumberField,
    NumberInput,
    SimpleForm,
    TabbedForm,
    FormTab,
    TextInput,
    ReferenceInput,
    ReferenceManyField,
    SelectInput,
    FunctionField,
    SelectArrayInput,
    BooleanInput,
    BooleanField,
} from 'react-admin';
import { AddContractTemplateButton } from './components/add-contract-template-button';

const ProductionTitle = props => {
    return <span>{props.record ? `${props.record.name}` : ''}</span>;
};

const rowStyle = (record, index) => ({
    backgroundColor: record.employments_quota !== null && record.active_employments_count > record.employments_quota ? '#fca5a5' : '#ffffff',
});

const toTitleCase = (str) => {
    return str
        .replace(/_/g, ' ')
        .replace(
            /\w\S*/g,
            text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
        );
};

const projectTypeChoices = [
    { id: 'high_end_tv', name: 'High End TV' },
    { id: 'major_motion_picture', name: 'Major Motion Picture' },
    { id: 'independent_motion_picture', name: 'Independent Motion Picture' },
    { id: 'unscripted', name: 'Unscripted' },
    { id: 'factual', name: 'Factual' },
    { id: 'continuing_drama', name: 'Continuing Drama' },
    { id: 'commercial', name: 'Commercial' },
    { id: 'animation', name: 'Animation' },
    { id: 'other', name: 'Other' },
];

const budgetBandChoices = [
    { id: '1', name: 'Band 1' },
    { id: '2', name: 'Band 2' },
    { id: '3', name: 'Band 3' },
    { id: '4', name: 'Band 4' },
];

const perDiemTypeChoices = [
    { id: 'meals', name: 'Meals' },
    { id: 'single', name: 'Single' },
    { id: 'shoot_non_shoot', name: 'Shoot / Non-Shoot' },
];

const timezoneChoices = [
    { id: 'UTC+0', name: 'UTC+0 — London / Edinburgh' },
    { id: 'UTC+1', name: 'UTC+1 — BST (British Summer Time)' },
    { id: 'UTC-5', name: 'UTC-5 — New York / Toronto' },
    { id: 'UTC-6', name: 'UTC-6 — Chicago / Mexico City' },
    { id: 'UTC-7', name: 'UTC-7 — Denver / Phoenix' },
    { id: 'UTC-8', name: 'UTC-8 — Los Angeles / Vancouver' },
    { id: 'UTC+1', name: 'UTC+1 — Paris / Berlin / Rome' },
    { id: 'UTC+2', name: 'UTC+2 — Athens / Helsinki' },
    { id: 'UTC+10', name: 'UTC+10 — Sydney / Melbourne' },
    { id: 'UTC+13', name: 'UTC+13 — Auckland / Wellington' },
];

const contractSendModeChoices = [
    { id: 'MANUAL', name: 'Manual - send each contract by hand' },
    { id: 'AUTO', name: 'Automatic - send as soon as the crew member accepts the deal' },
];

const currencyChoices = [
    { id: 'GBP', name: 'GBP — British Pound' },
    { id: 'USD', name: 'USD — US Dollar' },
    { id: 'EUR', name: 'EUR — Euro' },
    { id: 'CAD', name: 'CAD — Canadian Dollar' },
    { id: 'AUD', name: 'AUD — Australian Dollar' },
    { id: 'NZD', name: 'NZD — New Zealand Dollar' },
];

export const ProductionList = props => (
    <List {...props} resource="productions">
        <Datagrid rowClick="show" rowStyle={rowStyle}>
            <TextField source="id" />
            <TextField source="company.name" label="Company" />
            <TextField source="name" />
            <TextField source="address" />
            <TextField source="project_type" label="Project Type" />
            <DateField source="created_at" />
            <TextField source="admins.length" label="Admins" />
            <FunctionField
                label="Timesheet Template"
                render={record => toTitleCase(record?.timesheet_pdf_template || 'Standard')}
            />
            <FunctionField
                label="Disabled Fields"
                render={record => {
                    if (!record.disabled_fields || record.disabled_fields.length === 0) {
                        return 'None';
                    }

                    return record.disabled_fields.map(field =>
                        field.charAt(0).toUpperCase() + field.slice(1)
                    ).join(', ').replaceAll('_', ' ');
                }}
            />
            <FunctionField label="Employments Quota" render={record => record.employments_quota ?? '—'} />
            <FunctionField
                label="E-sign Allowance"
                render={record => record.esign_allowance == null
                    ? `${record.esign_used ?? 0} used (unlimited)`
                    : `${record.esign_used ?? 0} used, ${Math.max(0, record.esign_allowance - (record.esign_used ?? 0))} remaining`}
            />
            <BooleanField source="deal_flow_enabled" label="Deal Approvals" />
            <FunctionField
                label="Contract Sending"
                render={record => record.deal_flow_enabled ? toTitleCase(record.contract_send_mode || 'MANUAL') : '—'}
            />
            <BooleanField source="has_onboarding" label="Onboarding" />
            <BooleanField source="notices_enabled" label="Notices" />
            <NumberField source="active_employments_count" label="Active Employments" />
        </Datagrid>
    </List>
);

export const ProductionEdit = props => {
    const transform = (data) => {
        data.company_id = data.company ? data.company.id : undefined;
        delete data.company;
        delete data.admins;
        delete data.timesheet_weeks;
        delete data.departments;
        delete data.penalty_types;
        delete data.active_employments_count;
        delete data.esign_used;   // read-only, computed server-side — never submit it back
        return data;
    };

    const timesheetTemplateChoices = [
        { id: 'standard', value: 'Standard' },
        { id: 'single_per_diem', value: 'Single Per Diem' },
        { id: 'shooting_per_diem', value: 'Shooting Per Diem' },
    ];

    return (
        <Edit {...props} title={<ProductionTitle />} undoable={false} transform={transform}>
            <TabbedForm>
                <FormTab label="Details">
                    <TextInput source="name" fullWidth />
                    <TextInput multiline source="address" fullWidth />
                    <ReferenceInput label="Company" source="company.id" reference="companies" allowEmpty>
                        <SelectInput optionText="name" />
                    </ReferenceInput>
                    <SelectInput source="project_type" choices={projectTypeChoices} allowEmpty />
                    <SelectInput source="budget_band" choices={budgetBandChoices} allowEmpty helperText="HETV only" />
                    <TextInput source="other_description" fullWidth helperText="Required when project type is Other" />

                    <BooleanInput source="base_address_same_as_registered" label="Base address same as registered" />
                    <TextInput source="base_address_1" label="Base Address Line 1" fullWidth />
                    <TextInput source="base_address_2" label="Base Address Line 2" fullWidth />
                    <TextInput source="base_address_city" label="Base City" />
                    <TextInput source="base_address_county" label="Base County" />
                    <TextInput source="base_address_postcode" label="Base Postcode" />
                    <TextInput source="base_address_country" label="Base Country" />

                    <SelectInput source="timezone" choices={timezoneChoices} allowEmpty />
                    <SelectInput source="currency" choices={currencyChoices} allowEmpty />

                    <NumberInput source="mileage_rate" helperText="Per mile rate" />
                    <NumberInput source="holiday_pay_percentage" helperText="e.g. 12.07" />
                    <NumberInput source="meal_allowance_rate" />

                    <SelectInput source="per_diem_type" choices={perDiemTypeChoices} allowEmpty />
                    <NumberInput source="per_diem_breakfast_rate" />
                    <NumberInput source="per_diem_lunch_rate" />
                    <NumberInput source="per_diem_dinner_rate" />
                    <NumberInput source="per_diem_single_rate" />
                    <NumberInput source="per_diem_non_shoot_rate" />
                    <NumberInput source="per_diem_shoot_rate" />

                    <SelectInput source="timesheet_pdf_template" choices={timesheetTemplateChoices} optionText="value" optionValue="id" />
                    <SelectArrayInput
                        source="disabled_fields"
                        choices={[
                            { id: 'mileage', name: 'Mileage' },
                            { id: 'allowance', name: 'Allowance' },
                            { id: 'consecutive_days', name: 'Consecutive Days' },
                        ]}
                        multiple={true}
                    />
                    <NumberInput source="employments_quota" helperText="Leave empty to not enforce a quota" />
                    <NumberInput source="esign_allowance" helperText="Leave empty for unlimited" />
                    <FunctionField
                        label="E-sign Allowance"
                        render={record => record.esign_allowance == null
                            ? `${record.esign_used ?? 0} used (unlimited)`
                            : `${record.esign_used ?? 0} used, ${Math.max(0, record.esign_allowance - (record.esign_used ?? 0))} remaining`}
                    />

                    <BooleanInput
                        source="deal_flow_enabled"
                        label="Deal approvals"
                        helperText="Employments are saved as drafts, sent through the deal approval chain, then accepted by the crew member before a contract can be sent. Existing deals are not affected."
                    />
                    <SelectInput
                        source="contract_send_mode"
                        label="Contract sending"
                        choices={contractSendModeChoices}
                        defaultValue="MANUAL"
                        helperText="Only applies to deals accepted after this is changed"
                    />

                    <BooleanInput
                        source="has_onboarding"
                        label="Onboarding"
                        helperText="Crew complete a start form. Saving a change here switches it on or off for every employment on this production, existing ones included, overriding any set individually. Completed start forms are kept."
                    />

                    <BooleanInput
                        source="notices_enabled"
                        label="End of engagement notices"
                        helperText="Emails each crew member their notice automatically once their end date is within their notice period (7 days unless changed on the employment). Production admins can still give or rescind notice by hand when this is off. Employments whose end date has passed are ended automatically whether or not this is on."
                    />
                </FormTab>

                <FormTab label="Contract Templates">
                    <ReferenceManyField
                        reference="contract-templates"
                        target="production_id"
                        addLabel={false}
                        fullWidth
                    >
                        <Datagrid rowClick={(id) => `/contract-templates/${id}`}>
                            <TextField source="name" />
                            <BooleanField source="is_default" label="Default" />
                            <TextField source="signatureapi_template_url" label="Template URL" />
                            <DateField source="created_at" />
                        </Datagrid>
                    </ReferenceManyField>
                    <AddContractTemplateButton />
                </FormTab>
            </TabbedForm>
        </Edit>
    );
};
